import { deepLinkActivityFeedRouteHandler } from './deepLinkActivityFeedRouteHandler';
import { deepLinkAddMembersGroupHandler } from './deepLinkAddMembersGroupHandler';
import { deepLinkComposeRouteHandler } from './deepLinkComposeRouteHandler';
import { deepLinkDownloadMobileRouteHandler } from './deepLinkDownloadMobileRouteHandler';
import { deepLinkInboxRulesRouteHandler } from './deepLinkInboxRulesRouteHandler';
import { deepLinkInsightsRouteHandler } from './deepLinkInsightsRouteHandler';
import { deepLinkLocationCardRouteHandler } from './deepLinkLocationCardRouteHandler';
import { deepLinkOpenCreateGroupHandler } from './deepLinkOpenCreateGroupHandler';
import { deepLinkOpenDiscoverGroupHandler } from './deepLinkOpenDiscoverGroupHandler';
import { deepLinkOpenEditGroupHandler } from './deepLinkOpenEditGroupHandler';
import { deepLinkReadingPaneRouteHandler } from './deepLinkReadingPaneRouteHandler';
import { deepLinkRoomPanelRouteHandler } from './deepLinkRoomPanelRouteHandler';
import { deepLinkYammerRouteHandler } from './deepLinkYammerRouteHandler';
import { deepLinkQrConnectRouteHandler } from './deepLinkQrConnectRouteHandler';
import { COMPOSE_ROUTE, READ_ROUTE } from '../store/schema/DeeplinkRoute';
import type { DeeplinkView } from '../store/schema/DeeplinkView';
import getStore from '../store/store';
import type { BootstrapOptions } from 'owa-bootstrap';
import { lazySetupCalendarDeeplinkFunctionalBoot } from 'owa-calendar-post-boot';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import loc from 'owa-localize';
import {
    calendarDeeplinkModuleNameText,
    roomPanelDeeplinkModuleNameText,
    inboxRulesDeeplinkModuleNameText,
    locationCardDeeplinkModuleNameText,
    downloadMobileDeeplinkModuleNameText,
    activityFeedDeeplinkModuleNameText,
    meetingInsightsDeeplinkModuleNameText,
    createGroupDeeplinkModuleNameText,
    editGroupDeeplinkModuleNameText,
    addMembersDeeplinkModuleNameText,
    discoverGroupsDeeplinkModuleNameText,
    yammerDeeplinkModuleNameText,
    qrConnectDeeplinkModuleNameText,
} from '../strings.locstring.json';
import {
    lazySetupSharedOpxFunctionalBoot,
    lazySetupSharedOpxFunctionalBootWithNotifications,
} from 'owa-shared-opx-post-boot';
import {
    ROOM_PANEL_ROUTE,
    MEETING_INSIGHTS_ROUTE,
    LOCATION_CARD_ROUTE,
    CREATE_GROUP_ROUTE,
    ADD_MEMBERS_ROUTE,
    EDIT_GROUP_ROUTE,
    DISCOVER_GROUP_ROUTE,
    INBOX_RULES_ROUTE,
    DOWNLOAD_MOBILE_ROUTE,
    ACTIVITY_FEED_ROUTE,
    YAMMER_ROUTE,
    QR_CONNECT_ROUTE,
} from 'owa-opx-routes';
import { splitEmailAddress } from 'owa-calendar-forms-route-helpers';

type DeeplinkDefinition = {
    getRouteRegistrations: () => string[];
    getRouteSegments: () => string[];
    routeHandler: (parameters: any) => void;
    postLazyAction: BootstrapOptions['postLazyAction'];
    getModuleName: BootstrapOptions['getModuleName'];
};

type DeeplinkDefinitions = { [view in DeeplinkView]: DeeplinkDefinition };

const LOC_CALENDAR_DEEPLINK_MODULE_TEXT = () => loc(calendarDeeplinkModuleNameText);

// Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
// -> Error TS7006 (72,39): Parameter 'route' implicitly has an 'any' type.
// @ts-expect-error
function getDeeplinkRouteRegistration(route) {
    const deepLinkRoutePrefix = getDeeplinkRoutePrefix();
    return `/${deepLinkRoutePrefix}/${route}`;
}

export function getDeeplinkRoutePrefix() {
    return isHostAppFeatureEnabled('opxDeeplink') ? 'opxdeeplink' : 'deeplink';
}

export const DEEPLINK_DEFINITIONS: DeeplinkDefinitions = {
    ['Read']: {
        getRouteRegistrations: () => [
            getDeeplinkRouteRegistration(`${READ_ROUTE}/:itemId`),
            getDeeplinkRouteRegistration(
                `${READ_ROUTE}/account/:accountDomain/:accountAlias/:itemId`
            ),
            getDeeplinkRouteRegistration(`${READ_ROUTE}/group/:groupDomain/:groupAlias/:itemId`),
            getDeeplinkRouteRegistration(`${READ_ROUTE}/event/:iCalUId`),
        ],
        getRouteSegments: () => {
            const { itemId, account, group, iCalUId } = getStore();

            if (account && itemId) {
                const { alias, domain } = splitEmailAddress(account);
                return [getDeeplinkRoutePrefix(), READ_ROUTE, 'account', domain, alias, itemId];
            } else if (group && itemId) {
                const { alias, domain } = splitEmailAddress(group);
                return [getDeeplinkRoutePrefix(), READ_ROUTE, 'group', domain, alias, itemId];
            } else if (iCalUId) {
                return [getDeeplinkRoutePrefix(), READ_ROUTE, 'event', iCalUId];
            } else if (itemId) {
                return [getDeeplinkRoutePrefix(), READ_ROUTE, itemId];
            }

            // Unsupported route, should not be hit
            return [getDeeplinkRoutePrefix(), READ_ROUTE];
        },
        routeHandler: deepLinkReadingPaneRouteHandler,
        postLazyAction: lazySetupCalendarDeeplinkFunctionalBoot,
        getModuleName: LOC_CALENDAR_DEEPLINK_MODULE_TEXT,
    },
    ['Compose']: {
        getRouteRegistrations: () => [
            getDeeplinkRouteRegistration(COMPOSE_ROUTE),
            getDeeplinkRouteRegistration(`${COMPOSE_ROUTE}/:itemId`),
            getDeeplinkRouteRegistration(`${COMPOSE_ROUTE}/account/:account/:itemId`),
        ],
        getRouteSegments: () => {
            const { itemId, account } = getStore();
            return account && itemId
                ? [getDeeplinkRoutePrefix(), COMPOSE_ROUTE, 'account', account, itemId]
                : itemId
                ? [getDeeplinkRoutePrefix(), COMPOSE_ROUTE, itemId]
                : [getDeeplinkRoutePrefix(), COMPOSE_ROUTE];
        },
        routeHandler: deepLinkComposeRouteHandler,
        postLazyAction: lazySetupCalendarDeeplinkFunctionalBoot,
        getModuleName: LOC_CALENDAR_DEEPLINK_MODULE_TEXT,
    },
    ['RoomPanel']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(ROOM_PANEL_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), ROOM_PANEL_ROUTE],
        routeHandler: deepLinkRoomPanelRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(roomPanelDeeplinkModuleNameText),
    },
    ['MeetingInsights']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(MEETING_INSIGHTS_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), MEETING_INSIGHTS_ROUTE],
        routeHandler: deepLinkInsightsRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(meetingInsightsDeeplinkModuleNameText),
    },
    ['LocationCard']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(LOCATION_CARD_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), LOCATION_CARD_ROUTE],
        routeHandler: deepLinkLocationCardRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(locationCardDeeplinkModuleNameText),
    },
    ['CreateGroup']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(CREATE_GROUP_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), CREATE_GROUP_ROUTE],
        routeHandler: deepLinkOpenCreateGroupHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(createGroupDeeplinkModuleNameText),
    },
    ['AddMembers']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(ADD_MEMBERS_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), ADD_MEMBERS_ROUTE],
        routeHandler: deepLinkAddMembersGroupHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(addMembersDeeplinkModuleNameText),
    },
    ['EditGroup']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(EDIT_GROUP_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), EDIT_GROUP_ROUTE],
        routeHandler: deepLinkOpenEditGroupHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(editGroupDeeplinkModuleNameText),
    },
    ['DiscoverGroup']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(DISCOVER_GROUP_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), DISCOVER_GROUP_ROUTE],
        routeHandler: deepLinkOpenDiscoverGroupHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(discoverGroupsDeeplinkModuleNameText),
    },
    ['InboxRules']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(INBOX_RULES_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), INBOX_RULES_ROUTE],
        routeHandler: deepLinkInboxRulesRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(inboxRulesDeeplinkModuleNameText),
    },
    ['DownloadMobile']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(DOWNLOAD_MOBILE_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), DOWNLOAD_MOBILE_ROUTE],
        routeHandler: deepLinkDownloadMobileRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(downloadMobileDeeplinkModuleNameText),
    },
    ['ActivityFeed']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(ACTIVITY_FEED_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), ACTIVITY_FEED_ROUTE],
        routeHandler: deepLinkActivityFeedRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(activityFeedDeeplinkModuleNameText),
    },
    ['Yammer']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(YAMMER_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), YAMMER_ROUTE],
        routeHandler: deepLinkYammerRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(yammerDeeplinkModuleNameText),
    },
    ['QrConnect']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(QR_CONNECT_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), QR_CONNECT_ROUTE],
        routeHandler: deepLinkQrConnectRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(qrConnectDeeplinkModuleNameText),
    },
};
